import React, { useState, useEffect, useContext } from 'react';
import Chart from 'react-apexcharts';
import withStyles from '@material-ui/core/styles/withStyles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import AccessTime from '@mui/icons-material/AccessTime';
import Beenhere from '@mui/icons-material/Beenhere';
import MonetizationOn from '@mui/icons-material/MonetizationOn';
import EventAvailable from '@mui/icons-material/EventAvailable';
import Event from '@mui/icons-material/Event';
import CallMade from '@mui/icons-material/CallMade';
import RecentActors from '@mui/icons-material/RecentActors';
import Assignment from '@mui/icons-material/Assignment';
import Work from '@mui/icons-material/Work';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AlarmOff from '@mui/icons-material/AlarmOff';
import TextSMSIcon from '@mui/icons-material/Textsms';
import History from '@mui/icons-material/History';
import AssignmentLate from '@mui/icons-material/AssignmentLate';
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn';
import Ballot from '@mui/icons-material/Ballot';
import LiveHelp from '@mui/icons-material/LiveHelp';
import Poll from '@mui/icons-material/Poll';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningIcon from '@mui/icons-material/Warning';
import { Textfit } from 'react-textfit';
import LinearProgress from '@material-ui/core/LinearProgress';
import ReactCardFlip from 'react-card-flip';
import Tooltip from '@material-ui/core/Tooltip';
import Card from '../../components/GraphCard/components/Card.jsx';
import CardHeader from '../../components/GraphCard/components/CardHeader.jsx';
import CardBody from '../../components/GraphCard/components/CardBody.jsx';
import CardFooter from '../../components/GraphCard/components/CardFooter.jsx';
import { MuiThemeProvider } from '@material-ui/core';
import { ThemeContext } from '@emotion/react';
import dashboardStyle from './assets/dashboardStyle.jsx';

import { createMuiTheme } from '@material-ui/core/styles';

function CardChart(props) {
  const { classes, auto_refresh, alert, type, id } = props;
  const [state, setState] = useState({
    loading: props.loading,
    location: props.location,
    provider: props.provider,
    time_span: props.time_span,
    start_time_span: props.start_time_span,
    end_time_span: props.end_time_span,
    minimumUpdateThreshold: 60,
    last_refresh: 1,
    time_summary: '1 second',
    color: 'info',
    data: '',
    suffix: '',
    prefix: '',
    last_true_update: null,
    num_of_retries: 0,
    auto_refresh_disabled: false,
    isCardFlipped: false,
    graphOpacity: 0,
    historical: [],
    historical_labels: [],
    data_set_max: null,
    graphs_enabled: true,
  });

  const theme = createMuiTheme({
    palette: {
      primary: { main: '#1e8555', secondary: '#f5f5f5' },
    },
    typography: {
      useNextVariants: true,
      fontFamily: '"Raleway", "Helvetica", "Arial", sans-serif',
    },
  });

  const { finalTheme } = useContext(ThemeContext);

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    if (state.num_of_retries < 1) {
      setState((prevState) => ({ ...prevState, loading: true }));
      let location = state.location !== 'all' ? state.location : 'all';
      let provider = state.provider !== 'all' ? state.provider : 'all';
      let start_time_span = state.time_span;
      let end_time_span = state.end_time_span;

      if (start_time_span === 'custom') {
        start_time_span = state.start_time_span;
        end_time_span = state.end_time_span;
      }
      if (start_time_span === 'all_time') {
        start_time_span = props.created_at ? props.created_at.substring(0, 10) : '2016-01-01';
        end_time_span = new Date().toISOString().slice(0, 10);
      }

      const apiURL = `/api/dashboard_v2/metric/${props.type}/${start_time_span}/${location}/${provider}/${end_time_span}`;
      fetch(apiURL, { cache: 'no-store' })
        .then((response) => response.json())
        .then((responseJson) => {
          const data = responseJson.data;
          if (data) {
            const last = state.last_true_update;
            setState((prevState) => ({
              ...prevState,
              loading: false,
              color: data.color,
              data: data.value,
              historical: data.historical || [],
              historical_labels: data.historical_labels || [],
              data_set_max: data.data_set_max,
              suffix: data.suffix,
              prefix: data.prefix,
              last_true_update: new Date(),
              last_refresh: 1,
              num_of_retries: 0,
              time_summary: '1 second',
            }));
            if (last == null) {
              setTimeout(() => {
                countSeconds();
              }, 1000);
            }
          } else {
            setState((prevState) => ({ ...prevState, logged_out: true }));
          }
        })
        .catch((error) => {
          console.error(error);
          setState((prevState) => ({
            ...prevState,
            loading: false,
            data: 'N/A',
            suffix: '',
            prefix: '',
            num_of_retries: prevState.num_of_retries + 1,
          }));
          if (state.num_of_retries > 2) {
            setState((prevState) => ({ ...prevState, logged_out: true }));
          }
        });
    }
  };

  const refreshData = (force) => {
    if (state.num_of_retries < 1) {
      const now = new Date();
      now.setSeconds(now.getSeconds() - state.minimumUpdateThreshold);
      const last_true_update = new Date(state.last_true_update);
      if ((now > last_true_update && state.last_true_update != null) || force != null) {
        getData();
      } else {
        setState((prevState) => ({ ...prevState, loading: true }));
        setTimeout(() => {
          setState((prevState) => ({ ...prevState, loading: false, last_refresh: 1, time_summary: '1 second' }));
        }, 2000);
      }
    }
  };

  const countSeconds = () => {
    if (state.num_of_retries < 1) {
      if (props.auto_refresh) {
        const rem = props.refresh_timer - state.last_refresh;
        if (rem < 0) {
          refreshData(null);
        }
      }
      setState((prevState) => ({ ...prevState, last_refresh: prevState.last_refresh + 1 }));
      setTimeout(() => {
        countSeconds();
      }, 1000);
      summarizeTime();
    }
  };

  const summarizeTime = () => {
    let v, ps, pm, ph, pd, min, hou, sec, days;
    v = props.auto_refresh ? props.refresh_timer - state.last_refresh : state.last_refresh;

    if (v === 2) {
      setState((prevState) => ({ ...prevState, time_summary: '2 seconds' }));
    }
    if (v > 2 && v < 30 && v <= 3599) {
      if (state.time_summary !== 'a few seconds') {
        setState((prevState) => ({ ...prevState, time_summary: 'a few seconds' }));
      }
    }
    if (v > 30 && v < 60 && v <= 3599) {
      if (state.time_summary !== 'about 1 minute') {
        setState((prevState) => ({ ...prevState, time_summary: 'about 1 minute' }));
      }
    }
    if (v >= 60 && v <= 3599) {
      min = Math.floor(v / 60);
      sec = v - min * 60;
      pm = min > 1 ? 's' : '';
      ps = sec > 1 ? 's' : '';
      if (state.time_summary !== 'about ' + min + ' minute' + pm) {
        setState((prevState) => ({ ...prevState, time_summary: 'about ' + min + ' minute' + pm }));
      }
    }

    if (v >= 3600 && v <= 86399) {
      hou = Math.floor(v / 3600);
      min = Math.floor((v - hou * 3600) / 60);
      ph = hou > 1 ? 's' : '';
      pm = min > 1 ? 's' : '';
      if (state.time_summary !== 'about ' + hou + ' hour' + ph) {
        setState((prevState) => ({ ...prevState, time_summary: 'about ' + hou + ' hour' + ph }));
      }
    }

    if (v >= 86400) {
      days = Math.floor(v / 86400);
      hou = Math.floor((v - days * 86400) / 60 / 60);
      pd = days > 1 ? 's' : '';
      ph = hou > 1 ? 's' : '';
      if (state.time_summary !== days + ' day' + pd) {
        setState((prevState) => ({ ...prevState, time_summary: days + ' day' + pd }));
      }
    }
  };

  const handleOpenMenuClick = (event) => {
    setState((prevState) => ({ ...prevState, anchorElCard: event.currentTarget }));
  };

  const clearMenu = () => {
    setState((prevState) => ({ ...prevState, anchorElCard: null }));
  };

  const flipCard = (e) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isCardFlipped: !prevState.isCardFlipped,
      graphOpacity: !prevState.graphOpacity,
    }));
  };

  const renderIcon = (param) => {
    switch (param) {
      case 'AlarmOff':
        return <AlarmOff />;
      case 'Beenhere':
        return <Beenhere />;
      case 'MonetizationOn':
        return <MonetizationOn />;
      case 'Event':
        return <Event />;
      case 'EventAvailable':
        return <EventAvailable />;
      case 'CallMade':
        return <CallMade />;
      case 'RecentActors':
        return <RecentActors />;
      case 'Assignment':
        return <Assignment />;
      case 'PermPhoneMsgIcon':
        return <PermPhoneMsgIcon />;
      case 'TextSMS':
        return <TextSMSIcon />;
      case 'SupervisorAccountIcon':
        return <SupervisorAccountIcon />;
      case 'AssignmentTurnedIn':
        return <AssignmentTurnedIn />;
      case 'AssignmentLate':
        return <AssignmentLate />;
      case 'History':
        return <History />;
      case 'Work':
        return <Work />;
      case 'Ballot':
        return <Ballot />;
      case 'LiveHelp':
        return <LiveHelp />;
      case 'Poll':
        return <Poll />;
      default:
        return '';
    }
  };

  const generateCard = (props) => {
    switch (props.size) {
      case 'large':
        return renderLargeCard(props);
      case 'small':
      default:
        return renderSmallCard(props);
    }
  };

  const generateGraph = (props) => {
    switch (props.type) {
      case 'fills_by_type':
        return renderDonutChart(props);
      case 'schedule_balancing_stats':
        return renderBarChart(props);
      case 'stats_by_appointment_type':
        return renderStackedBarChart(props);
      case 'stats_by_location':
        return renderBarChart(props);
      case 'stats_by_provider':
        return renderBarChart(props);
      case 'top_cancelled_appointment_time':
        return renderHeatChart(props);
      case 'top_cancelled_appointment_day':
        return renderVerticalBarChart(props);
      case 'top_booked_provider':
        return renderBarChart(props);
      case 'top_booked_appointment_type':
        return renderBarChart(props);
      case 'top_providers_to_add':
        return renderBarChart(props);
      case 'top_appointment_types_to_add':
        return renderBarChart(props);
      case 'small':
      default:
        return renderLineChart(props);
    }
  };

  const defaultNoDataOptions = {
    text: 'Not enough data within chosen time-span',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: '#fff',
      fontSize: '14px',
    },
  };

  const defaultGridOptions = {
    row: {
      colors: ['#f3f3f3', 'transparent'],
      opacity: 0.3,
    },
  };

  const defaultToolbarOptions = {
    show: true,
    tools: {
      download: true,
      selection: false,
      zoom: false,
      zoomin: false,
      zoomout: false,
      pan: false,
      reset: false,
    },
  };

  const defaultdropShadowOptions = {
    enabled: true,
    top: 0,
    left: 0,
    blur: 0,
    opacity: 0.6,
  };

  const defaultGraphColors = ['#13324c', '#e4346b', '#635b81', '#f9c82a', '#ED5564', '#546E7A', '#ED5564'];

  const renderLineChart = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <Chart
          options={{
            chart: {
              toolbar: defaultToolbarOptions,
              dropShadow: defaultdropShadowOptions,
            },
            tooltip: {
              theme: 'dark',
              x: {
                show: false,
              },
            },
            stroke: {
              curve: 'smooth',
            },
            legend: {
              position: 'top',
              offsetY: 10,
            },
            yaxis: {
              labels: {
                show: true,
              },
              categories: props.historical,
            },
            xaxis: {
              position: 'bottom',
              offsetY: -1,
              labels: {
                formatter: function (value) {
                  return value;
                },
                show: true,
                trim: false,
              },
              categories: props.historical_labels,
            },
            grid: defaultGridOptions,
            noData: defaultNoDataOptions,
            colors: defaultGraphColors,
            title: {
              text: props.title,
            },
          }}
          series={[
            { name: 'Total', data: props.historical.total },
            { name: 'Filled', data: props.historical.filled },
          ]}
          type="line"
          height={183}
          style={{ marginTop: -10, marginBottom: -30 }}
        />
      </MuiThemeProvider>
    );
  };

  const renderPieChart = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <Chart
          options={{
            tooltip: {
              theme: 'dark',
            },
            plotOptions: {
              pie: {
                expandOnClick: false,
                dataLabels: {
                  offset: 15,
                },
              },
            },
            labels: props.historical_labels,
            colors: defaultGraphColors,
            responsive: [
              {
                breakpoint: 480,
                options: {
                  chart: {
                    width: 200,
                  },
                  legend: {
                    position: 'bottom',
                  },
                },
              },
            ],
          }}
          series={props.historical}
          type="pie"
          height={160}
        />
      </MuiThemeProvider>
    );
  };

  const renderDonutChart = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <Chart
          options={{
            tooltip: {
              theme: 'dark',
            },
            legend: {
              position: 'left',
            },
            plotOptions: {
              pie: {
                expandOnClick: false,
                donut: {
                  size: '71%',
                  labels: {
                    show: true,
                    name: {
                      show: false,
                    },
                    value: {
                      show: true,
                      offsetY: -1,
                    },
                    total: {
                      show: true,
                      label: 'Total:',
                    },
                  },
                },
              },
            },
            labels: props.historical_labels,
            colors: defaultGraphColors,
            dataLabels: {
              enabled: false,
            },
            responsive: [
              {
                breakpoint: 1555,
                options: {
                  chart: {
                    height: 135,
                  },
                },
              },
              {
                breakpoint: 1480,
                options: {
                  chart: {
                    height: 130,
                  },
                },
              },
              {
                breakpoint: 1460,
                options: {
                  chart: {
                    height: 160,
                  },
                  legend: {
                    position: 'top',
                  },
                },
              },
              {
                breakpoint: 1000,
                options: {
                  chart: {
                    height: 160,
                  },
                  legend: {
                    position: 'top',
                  },
                },
              },
            ],
            noData: defaultNoDataOptions,
          }}
          series={props.historical}
          type="donut"
          height={160}
        />
      </MuiThemeProvider>
    );
  };

  const renderBarChart = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <Chart
          options={{
            chart: {
              toolbar: defaultToolbarOptions,
              dropShadow: defaultdropShadowOptions,
            },
            colors: defaultGraphColors,
            plotOptions: {
              bar: {
                horizontal: true,
                barHeight: '96%',
                columnWidth: '100%',
              },
            },
            dataLabels: {
              enabled: false,
            },
            tooltip: {
              theme: 'dark',
              x: {
                show: false,
              },
              y: {
                title: {
                  formatter: function () {
                    return '';
                  },
                },
              },
            },
            grid: defaultGridOptions,
            xaxis: {
              categories: props.historical_labels,
            },
            noData: defaultNoDataOptions,
            title: {
              text: props.title,
            },
          }}
          series={[{ name: '', data: props.historical }]}
          type="bar"
          height={197}
          style={{ marginTop: -15, marginBottom: -37 }}
        />
      </MuiThemeProvider>
    );
  };

  const renderVerticalBarChart = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <Chart
          options={{
            chart: {
              toolbar: defaultToolbarOptions,
              dropShadow: defaultdropShadowOptions,
            },
            colors: defaultGraphColors,
            plotOptions: {
              bar: {
                barHeight: '100%',
              },
            },
            dataLabels: {
              enabled: false,
            },
            tooltip: {
              theme: 'dark',
              x: {
                show: false,
              },
              y: {
                title: {
                  formatter: function () {
                    return '';
                  },
                },
              },
            },
            grid: defaultGridOptions,
            xaxis: {
              categories: props.historical_labels,
            },
            noData: defaultNoDataOptions,
            title: {
              text: props.title,
            },
          }}
          series={[{ name: '', data: props.historical }]}
          type="bar"
          height={194}
          style={{ marginTop: -15, marginBottom: -37 }}
        />
      </MuiThemeProvider>
    );
  };

  const renderStackedBarChart = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <Chart
          options={{
            chart: {
              stacked: true,
              toolbar: defaultToolbarOptions,
              dropShadow: defaultdropShadowOptions,
            },
            colors: defaultGraphColors,
            plotOptions: {
              bar: {
                horizontal: true,
                barHeight: '96%',
                columnWidth: '100%',
              },
            },
            tooltip: {
              theme: 'dark',
              x: {
                show: false,
              },
              y: {
                title: {
                  formatter: function () {
                    return '';
                  },
                },
              },
            },
            dataLabels: {
              enabled: false,
            },
            stroke: {
              width: 1,
              colors: ['#fff'],
            },
            legend: {
              position: 'top',
            },
            yaxis: {
              title: {
                text: undefined,
              },
            },
            xaxis: {
              categories: props.historical_labels,
            },
            noData: defaultNoDataOptions,
            title: {
              text: props.title,
            },
          }}
          series={props.historical}
          type="bar"
          height={188}
          style={{ marginTop: -10, marginBottom: -35 }}
        />
      </MuiThemeProvider>
    );
  };

  const renderHeatChart = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <Chart
          options={{
            chart: {
              type: 'heatmap',
              toolbar: defaultToolbarOptions,
              dropShadow: defaultdropShadowOptions,
            },
            stroke: {
              width: 1,
            },
            plotOptions: {
              heatmap: {
                enableShades: false,
                colorScale: {
                  ranges: [
                    {
                      from: 0,
                      to: 1,
                      name: 'none',
                      color: '#DADEDF',
                    },
                    {
                      from: 1,
                      to: Math.round(props.data_set_max / 3),
                      name: 'low',
                      color: '#13324c',
                    },
                    {
                      from: Math.round(props.data_set_max / 3 + 1),
                      to: Math.round((props.data_set_max / 3) * 2),
                      name: 'medium',
                      color: '#f9c82a',
                    },
                    {
                      from: Math.round((props.data_set_max / 3) * 2 + 1),
                      to: 99999,
                      name: 'high',
                      color: '#e4346b',
                    },
                  ],
                },
              },
            },
            xaxis: {
              type: 'category',
              categories: [
                '7 AM',
                '8 AM',
                '9 AM',
                '10 AM',
                '11 AM',
                '12 PM',
                '1 PM',
                '2 PM',
                '3 PM',
                '4 PM',
                '5 PM',
                '6 PM',
              ],
            },
            dataLabels: {
              enabled: false,
            },
            tooltip: {
              theme: 'dark',
              x: {
                show: false,
                title: {
                  formatter: function () {
                    return 'time';
                  },
                },
              },
              y: {
                show: false,
                title: {
                  formatter: function () {
                    return 'total';
                  },
                },
              },
            },
            noData: defaultNoDataOptions,
            title: {
              text: props.title,
            },
          }}
          series={props.historical}
          type="heatmap"
          height={190}
          style={{ marginTop: -10, marginBottom: -35 }}
        />
      </MuiThemeProvider>
    );
  };

  const renderSmallCard = (props) => {
    let { data, suffix, prefix, lines, graphOpacity, historical, time_span, graphs_enabled } = props;
    data = String(data);
    let titles;
    switch (time_span) {
      case 'today':
        titles = ['Y', 'T'];
        break;
      case 'last_week':
        titles = ['', '', '', '', '', '', ''];
        break;
      case 'last_month':
        titles = ['W1', 'W2', 'W3', 'W4'];
        break;
      case 'all_time':
      case 'custom':
      default:
        titles = [];
        for (let i = 1; i < historical.length + 1; i++) {
          titles.push('');
        }
        break;
    }
    return (
      <MuiThemeProvider theme={finalTheme}>
        <ReactCardFlip
          isFlipped={state.isCardFlipped}
          flipDirection="horizontal"
        >
          <div
            onClick={flipCard}
            style={{ display: 'flex', flexDirection: 'column' }}
            key="front"
          >
            <CardBody style={{ paddingTop: 10, paddingLeft: 10, width: '65%', height: 70, alignSelf: 'flex-start' }}>
              <h2
                className={classes.cardTitle}
                style={{ fontSize: '1.3em' }}
              >
                {props.title}
              </h2>
              <p className={classes.cardCategory}> </p>
            </CardBody>
            <CardHeader
              color={props.color}
              style={{
                cursor: 'pointer',
                height: 90,
                padding: 10,
                width: '35%',
                alignSelf: 'flex-end',
                marginTop: -80,
                marginBottom: 10,
              }}
            >
              <span style={{ width: '100%', height: 40, textAlign: 'center', display: 'block' }}>
                <Textfit
                  mode={lines === 'double' ? 'double' : 'single'}
                  min={10}
                  max={32}
                >
                  {data !== 'N/A' ? (
                    <>
                      {prefix}
                      {data.includes('%') ? (
                        Number(data.slice(0, -1)).toLocaleString() + '%'
                      ) : (
                        <>
                          {lines === 'double' ? (
                            <>
                              {Number(data.toLocaleString())}
                              <br />
                            </>
                          ) : (
                            Number(data).toLocaleString()
                          )}
                        </>
                      )}
                      {suffix}
                    </>
                  ) : (
                    <>N/A</>
                  )}
                </Textfit>
              </span>
              <span style={{ marginTop: 5, textAlign: 'center', display: 'block' }}>{renderIcon(props.icon)}</span>
            </CardHeader>
          </div>

          <div
            key="back"
            onClick={flipCard}
          >
            {state.isCardFlipped ? (
              <CardBody
                style={{
                  opacity: graphOpacity,
                  paddingTop: 0,
                  paddingLeft: 10,
                  width: '100%',
                  height: 80,
                  alignSelf: 'flex-start',
                }}
              >
                <h2
                  className={classes.cardTitleSmall}
                  style={{ fontSize: '1.05em' }}
                >
                  {props.title}
                </h2>
                {titles.length > 0 && historical.length && historical.length > 0 ? (
                  <Chart
                    options={{
                      chart: {
                        toolbar: {
                          show: false,
                        },
                      },
                      colors: defaultGraphColors,
                      yaxis: {
                        labels: {
                          show: false,
                        },
                      },
                      xaxis: {
                        position: 'bottom',
                        labels: {
                          formatter: function (value) {
                            return value;
                          },
                          show: true,
                          trim: true,
                        },
                        tooltip: {
                          enabled: false,
                        },
                        categories: titles,
                      },
                    }}
                    series={[{ name: '', data: historical }]}
                    type="line"
                    width={'100%'}
                    height={90}
                  />
                ) : (
                  <></>
                )}
              </CardBody>
            ) : (
              <></>
            )}
          </div>
        </ReactCardFlip>
      </MuiThemeProvider>
    );
  };

  const renderLargeCard = (props) => {
    return (
      <MuiThemeProvider theme={finalTheme}>
        <>
          <CardHeader color={props.color}>
            <div className="chart_container">{generateGraph(props)}</div>
          </CardHeader>
          <CardBody>
            <h4 className={classes.cardTitle}>{props.title}</h4>
          </CardBody>
        </>
      </MuiThemeProvider>
    );
  };

  const { loading, time_summary, anchorElCard } = state;

  return (
    <MuiThemeProvider theme={finalTheme}>
      {!loading ? (
        <Card
          style={{ backgroundColor: finalTheme?.palette?.background?.paper, color: finalTheme?.palette?.text?.primary }}
          chart
        >
          {generateCard({ ...props, ...state })}
          <CardFooter chart>
            <div className={classes.stats + ' refreshSpan'}>
              <span style={{ fontSize: 10 }}>
                <AccessTime />
                {state.num_of_retries < 1 ? (
                  <>{auto_refresh ? <>Updating in {time_summary}</> : <>Updated {time_summary} ago</>}</>
                ) : (
                  <>Refresh failed, check login</>
                )}
                .&nbsp;&nbsp;
              </span>
              <span
                className={'refreshSpans'}
                onClick={() => {
                  refreshData(null);
                }}
                style={{ display: 'none', fontSize: 9, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Refresh Now
              </span>
            </div>
            <div className={classes.stats + ' graph_card_extras'}>
              {alert ? (
                <Tooltip
                  placement="right"
                  title={alert}
                  aria-label={alert}
                >
                  <WarningIcon
                    style={{
                      display: 'block',
                      float: 'right',
                      marginLeft: 5,
                      marginBottom: 5,
                      marginTop: 0,
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
              <SettingsIcon
                onClick={handleOpenMenuClick}
                style={{
                  display: 'block',
                  float: 'right',
                  marginLeft: 5,
                  marginBottom: 5,
                  marginTop: 0,
                  cursor: 'pointer',
                }}
              />

              <Menu
                id="graph_card_extras"
                anchorEl={anchorElCard}
                open={anchorElCard != null ? true : false}
                style={{ marginLeft: 30 }}
                onClose={clearMenu}
              >
                <MenuItem
                  onClick={() => {
                    refreshData(null);
                    clearMenu();
                  }}
                >
                  Refresh Data
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    props.removeCard(type, id);
                    clearMenu();
                  }}
                >
                  Hide Card
                </MenuItem>
              </Menu>
            </div>
          </CardFooter>
        </Card>
      ) : (
        <Card chart>
          <LinearProgress
            color="primary"
            style={{ width: '80%', display: 'block', textAlign: 'center', margin: '17% auto 15% auto' }}
          />
        </Card>
      )}
    </MuiThemeProvider>
  );
}

export default withStyles(dashboardStyle)(CardChart);
